import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: "What is the main goal of the restorative space challenge?",
    options: [
      "Designing and implementing improvements in one environment",
      "Moving to a new location",
      "Buying expensive furniture"
    ],
    correctAnswer: 0,
    explanation: "The restorative space challenge involves selecting one environment, designing 3-5 improvements based on restorative design principles, implementing the changes, and evaluating the results."
  },
  {
    question: "What does environmental justice primarily address?",
    options: [
      "Fair distribution of environmental benefits and burdens",
      "Only wealthy communities having access to clean air",
      "Environmental regulations that favor corporations"
    ],
    correctAnswer: 0,
    explanation: "Environmental justice focuses on ensuring that all communities, regardless of socioeconomic status or race, have equal access to environmental benefits like clean air and water, while not bearing disproportionate environmental burdens."
  },
  {
    question: "Which of the following is a key principle of sustainable consumption?",
    options: [
      "Buying as many products as possible",
      "Reducing waste and choosing durable, long-lasting items",
      "Ignoring product origins and manufacturing processes"
    ],
    correctAnswer: 1,
    explanation: "Sustainable consumption involves making mindful choices about what we buy and use, focusing on reducing waste, choosing durable items, and considering the environmental impact of production and disposal."
  },
  {
    question: "What is a carbon footprint?",
    options: [
      "The size of a person's shoe",
      "The total greenhouse gas emissions caused by an individual or organization",
      "A type of environmental certification"
    ],
    correctAnswer: 1,
    explanation: "A carbon footprint measures the total greenhouse gas emissions caused directly and indirectly by an individual, organization, event, or product, expressed as carbon dioxide equivalent."
  },
  {
    question: "How can communities contribute to sustainable living?",
    options: [
      "Only through individual actions",
      "Through collective efforts like community gardens and local policies",
      "By avoiding all community involvement"
    ],
    correctAnswer: 1,
    explanation: "Communities can promote sustainable living through collective actions such as establishing community gardens, advocating for sustainable local policies, organizing clean-up events, and sharing resources and knowledge."
  }
];

const EnvironmentalModule4QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('environmentalM4');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 4 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                Environmental psychology principles
              </li>
              <li>
                Designing restorative spaces
              </li>
              <li>
                Environmental justice concepts
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Continue your environmental wellness journey by exploring sustainability and global responsibility!</p>
            <p>Ready to learn how environmental well-being connects to planetary health?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/environmental-resources')}
          >
            Return to Environmental Resources
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left environmental-panel">
          <div className="module-header">
            <h1>Module 4 Quiz</h1>
            <p className="module-subtitle">Sustainable Living Practices</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about sustainable living practices.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/environmental-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalModule4QuizPage;

